import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react"
import { createPortal } from "react-dom"
import { router } from "@inertiajs/react"
import type { RequestPayload } from "@inertiajs/core"
import { Trans, useLingui } from "@lingui/react/macro"
import { useAnchoredPosition } from "@/hooks/use-anchored-position"
import { methodLabel } from "@/lib/follow-up-method"

// Inline composer for creating (or editing) a deal's
// next-step Activity. Surfaces two variants:
//
//   - `inline` (desktop): a card that expands inside the
//     NEXT STEPS section, Paper FL1-0.
//   - `sheet` (mobile): a bottom sheet with the same
//     fields stacked vertically, Paper G2Y-0 / KVG-0.
//
// "Option B / Keep the fast path" layout (CON-255): the
// title is the hero, with a hairline divider beneath it;
// the quick-due chips + a custom date picker sit on their
// own row; the Method and Owner pickers collapse into a
// single DETAILS row — each shows only its current value
// (+ chevron) and opens its options on click.
//
// The form fields match the v2
// `V2::Deals::Activities::CreateForm` / `UpdateForm`
// payload: title, method, due_at, assignee_id.
// Quick-due chips compute concrete datetimes in the
// user's local timezone so a click here doesn't need a
// follow-up datepicker round-trip.

export const METHOD_OPTIONS: Array<{
  value: MethodValue
}> = [
  {value: "whatsapp_message"},
  {value: "voice_call"},
  {value: "email"},
]

export type MethodValue = "whatsapp_message" | "voice_call" | "email"

const MAX_CHARS = 120
const WARN_AT = 100

const PICKER_WIDTH = 200

export interface NextStepAssigneeOption {
  id: number
  name: string
  initials: string
}

export interface NextStepComposerEmployee {
  id: number
  name: string
  initials: string | null
}

export interface NextStepComposerInitialData {
  id?: number
  title?: string
  // A pending row may carry a method outside the three picker
  // options (a call-logged `voice_call_outbound`, …) or none —
  // keep it as the raw column value so it round-trips on edit.
  method?: string | null
  dueAtIso?: string | null
  assigneeId?: number | null
}

export interface NextStepComposerProps {
  deal: {id: number; name: string}
  currentEmployee: NextStepComposerEmployee
  ownerOptions?: NextStepAssigneeOption[]
  initial?: NextStepComposerInitialData
  variant?: "inline" | "sheet"
  autoFocus?: boolean
  onSaved?: () => void
  onCancel?: () => void
}

// Quick-due preset chips. The label is what the user
// reads ("Tomorrow AM"); the resolver builds a real
// `Date` at click time so the value is bound to the
// user's local timezone and DST.
const QUICK_DUE_PRESETS: Array<{
  key: string
  label: string
  resolve: () => Date
}> = [
  {
    key: "tomorrow_am",
    label: "Tomorrow AM",
    resolve: () => {
      const date = new Date()
      date.setDate(date.getDate() + 1)
      date.setHours(9, 0, 0, 0)
      return date
    },
  },
  {
    key: "in_2_days",
    label: "In 2 days",
    resolve: () => {
      const date = new Date()
      date.setDate(date.getDate() + 2)
      date.setHours(9, 0, 0, 0)
      return date
    },
  },
  {
    key: "next_week",
    label: "Next week",
    resolve: () => {
      const date = new Date()
      const daysUntilMonday = (8 - date.getDay()) % 7 || 7
      date.setDate(date.getDate() + daysUntilMonday)
      date.setHours(9, 0, 0, 0)
      return date
    },
  },
  {
    key: "in_2_weeks",
    label: "In 2 weeks",
    resolve: () => {
      const date = new Date()
      date.setDate(date.getDate() + 14)
      date.setHours(9, 0, 0, 0)
      return date
    },
  },
]

export function NextStepComposer({
  deal,
  currentEmployee,
  ownerOptions = [],
  initial,
  variant = "inline",
  autoFocus = true,
  onSaved,
  onCancel,
}: NextStepComposerProps) {
  const { t } = useLingui()
  const isEdit = Boolean(initial?.id)
  const [title, setTitle] = useState(initial?.title ?? "")
  // Editable method holds the row's RAW value so an existing
  // row round-trips even when its method isn't one of the three
  // picker options, or is absent. New rows default to WhatsApp;
  // edits preserve whatever the row had — including `null`, so
  // editing the due date can't silently assign a channel.
  const [method, setMethod] = useState<string | null>(
    initial?.method ?? (isEdit ? null : "whatsapp_message"),
  )
  const [dueAt, setDueAt] = useState<Date | null>(() =>
    initial?.dueAtIso ? new Date(initial.dueAtIso) : QUICK_DUE_PRESETS[0].resolve(),
  )
  const [activeQuickDue, setActiveQuickDue] = useState<string | null>(
    initial?.dueAtIso ? null : "tomorrow_am",
  )
  const [assigneeId, setAssigneeId] = useState<number>(
    initial?.assigneeId ?? currentEmployee.id,
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const titleRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (autoFocus) titleRef.current?.focus()
  }, [autoFocus])

  const charCount = title.length
  const overLimit = charCount > MAX_CHARS
  const nearLimit = charCount >= WARN_AT
  const disabled = submitting || title.trim() === "" || overLimit
  const counterClass = useMemo(() => {
    if (overLimit) return "text-destructive font-medium"
    return "text-foreground"
  }, [overLimit])

  const onQuickDue = (presetKey: string) => {
    const preset = QUICK_DUE_PRESETS.find((p) => p.key === presetKey)
    if (!preset) return
    setDueAt(preset.resolve())
    setActiveQuickDue(presetKey)
  }

  const onCustomDue = (localValue: string) => {
    if (!localValue) return
    const parsed = new Date(localValue)
    if (Number.isNaN(parsed.getTime())) return
    setDueAt(parsed)
    setActiveQuickDue(null)
  }

  const submit = () => {
    if (disabled) return
    setSubmitting(true)
    setError(null)

    const payload: Record<string, unknown> = {
      title: title.trim(),
      method,
      due_at: dueAt?.toISOString(),
      assignee_id: assigneeId,
    }
    const url = isEdit
      ? `/v2/deals/${deal.id}/activities/${initial!.id}`
      : `/v2/deals/${deal.id}/activities`
    const verb = isEdit ? router.patch.bind(router) : router.post.bind(router)

    verb(
      url,
      // Nested form payload; `RequestPayload` only models flat
      // `FormDataConvertible` values, so assert the nested
      // shape Inertia serializes at runtime.
      {activity: payload} as RequestPayload,
      {
        preserveScroll: true,
        onSuccess: () => {
          setSubmitting(false)
          if (!isEdit) {
            setTitle("")
          }
          onSaved?.()
        },
        onError: (errors) => {
          setSubmitting(false)
          const message =
            (errors as Record<string, string>).title ??
            (errors as Record<string, string>).method ??
            (errors as Record<string, string>).due_at ??
            (errors as Record<string, string>).base ??
            t`Couldn't save the next step. Try again.`
          setError(message)
        },
      },
    )
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      submit()
    } else if (e.key === "Escape") {
      e.preventDefault()
      onCancel?.()
    }
  }

  const allAssignees = useMemo(() => {
    const list: NextStepAssigneeOption[] = [...ownerOptions]
    if (!list.some((o) => o.id === currentEmployee.id)) {
      list.unshift({
        id: currentEmployee.id,
        name: currentEmployee.name,
        initials: currentEmployee.initials ?? currentEmployee.name.charAt(0),
      })
    }
    return list
  }, [ownerOptions, currentEmployee])

  const isSheet = variant === "sheet"

  return (
    <div
      data-testid="next-step-composer"
      className={
        isSheet
          ? "flex flex-col gap-4"
          : "flex flex-col rounded-xl bg-white border border-[#C9C2B6] p-4 gap-3 focus-within:ring-2 focus-within:ring-brand/15"
      }
    >
      {/* TITLE — the hero input. No leading circle (dead
          pre-save affordance); the counter only appears as
          the user nears the limit. */}
      {isSheet ? (
        <span className="tracking-[0.08em] uppercase font-medium text-[#9A938A] text-[10px] leading-3">
          <Trans>WHAT'S THE NEXT STEP?</Trans>
        </span>
      ) : null}
      <div className="flex items-center gap-2">
        <input
          ref={titleRef}
          type="text"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value)
            setError(null)
          }}
          onKeyDown={onKeyDown}
          placeholder={t`Confirm 18% counter-offer details`}
          disabled={submitting}
          aria-label={t`Next step title`}
          className="grow min-w-0 bg-transparent text-foreground text-[14px] leading-5 focus:outline-none placeholder:text-[#9A938A]"
        />
        {nearLimit ? (
          <span
            data-testid="next-step-char-counter"
            className={`shrink-0 tabular-nums text-[11px] leading-4 ${counterClass}`}
          >
            {charCount} / {MAX_CHARS}
          </span>
        ) : null}
      </div>

      {/* Hairline divider under the title. */}
      <div className="h-px bg-[#EDE7DB]" aria-hidden="true" />

      {/* DUE — quick presets on one row + a "Pick date"
          affordance for a custom datetime, with the resolved
          line below. No uppercase label. */}
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          {QUICK_DUE_PRESETS.map((preset) => (
            <Chip
              key={preset.key}
              active={activeQuickDue === preset.key}
              onClick={() => onQuickDue(preset.key)}
              data-testid={`next-step-due-${preset.key}`}
            >
              <span><Trans>{preset.label}</Trans></span>
            </Chip>
          ))}
          <CustomDuePicker
            active={activeQuickDue === null}
            value={dueAt}
            onChange={onCustomDue}
          />
        </div>
        {dueAt ? (
          <p
            data-testid="next-step-due-resolved"
            className="text-[#7A746B] text-[12px] leading-4 pl-1"
          >
            <Trans>Due {formatDueAt(dueAt)}</Trans>
          </p>
        ) : null}
      </div>

      {/* DETAILS — Method picker (left) + Owner picker (right),
          each collapsed to its current value. No uppercase
          labels. */}
      <div className="flex items-center gap-2">
        <MethodPicker method={method} onChange={setMethod} />
        <span className="grow" />
        <OwnerPicker
          assignees={allAssignees}
          currentEmployeeId={currentEmployee.id}
          assigneeId={assigneeId}
          onChange={setAssigneeId}
        />
      </div>

      {error ? (
        <p role="alert" className="text-destructive text-xs leading-4">
          {error}
        </p>
      ) : null}

      <div className="flex items-center gap-2 pt-1">
        {isSheet ? null : (
          <span className="text-[#9A938A] text-[11px] leading-4"><Trans>⌘+Enter to save</Trans></span>
        )}
        <span className="grow" />
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-full bg-transparent border border-[#C9C2B6] text-foreground text-[12px] leading-4 px-3 py-1.5 font-medium hover:bg-[#F7F5F0] disabled:opacity-60"
        >
          <Trans>Cancel</Trans>
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={disabled}
          data-testid="next-step-composer-save"
          className="rounded-full bg-[#1E1B17] text-[#F0EDE6] text-[12px] leading-4 px-3 py-1.5 font-semibold hover:bg-black disabled:opacity-60"
        >
          <Trans>Save next step</Trans>
        </button>
      </div>
    </div>
  )
}

// Collapsed Method picker — shows the current value's glyph +
// label + a chevron. Clicking opens a portaled options
// popover (so it escapes the slideover's clip), styled to
// match the shared OwnerPopover.
function MethodPicker({
  method,
  onChange,
}: {
  method: string | null
  onChange: (value: MethodValue) => void
}) {
  const { t, i18n } = useLingui()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  // A row may carry a method outside the three picker options
  // (call-logged variants) or none — render its real label via
  // the shared map, falling back to a prompt rather than a
  // misleading default. `methodLabel` is the canonical
  // (locale-aware) source for every method value.
  const triggerLabel = methodLabel(method, i18n) ?? t`Set method`

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t`Method`}
        data-testid="next-step-method-trigger"
        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#C9C2B6] text-foreground text-[12px] leading-4 px-3 py-1.5 font-medium hover:bg-[#F7F5F0]"
      >
        <MethodGlyph method={method} />
        <span><Trans>{triggerLabel}</Trans></span>
        <Chevron />
      </button>
      {open && triggerRef.current ? (
        <PickerPopover
          anchor={triggerRef.current}
          ariaLabel="Pick a method"
          onClose={() => setOpen(false)}
        >
          {METHOD_OPTIONS.map((option) => (
            <PickerOption
              key={option.value}
              active={method === option.value}
              onClick={() => {
                onChange(option.value)
                setOpen(false)
              }}
              testId={`next-step-method-${option.value}`}
            >
              <MethodGlyph method={option.value} />
              <span className="text-foreground text-[13px] leading-4">
                {methodLabel(option.value, i18n)}
              </span>
            </PickerOption>
          ))}
        </PickerPopover>
      ) : null}
    </>
  )
}

// Collapsed Owner picker — shows the current assignee's
// avatar + name (or "Me — <name>" for the current employee).
function OwnerPicker({
  assignees,
  currentEmployeeId,
  assigneeId,
  onChange,
}: {
  assignees: NextStepAssigneeOption[]
  currentEmployeeId: number
  assigneeId: number
  onChange: (id: number) => void
}) {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const current = assignees.find((a) => a.id === assigneeId)

  const label = (assignee: NextStepAssigneeOption) =>
    assignee.id === currentEmployeeId
      ? `Me — ${assignee.name}`
      : assignee.name

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t`Assignee`}
        data-testid="next-step-assignee-trigger"
        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#C9C2B6] text-foreground text-[12px] leading-4 px-3 py-1.5 font-medium hover:bg-[#F7F5F0] max-w-full min-w-0"
      >
        {current ? (
          <span className="flex items-center justify-center shrink-0 rounded-full size-4 bg-[#1E1B17] text-[#F0EDE6] font-semibold text-[8px] leading-3">
            {current.initials}
          </span>
        ) : null}
        <span className="truncate">
          {current ? label(current) : t`Assignee`}
        </span>
        <Chevron />
      </button>
      {open && triggerRef.current ? (
        <PickerPopover
          anchor={triggerRef.current}
          ariaLabel="Pick an owner"
          onClose={() => setOpen(false)}
        >
          {assignees.map((assignee) => (
            <PickerOption
              key={assignee.id}
              active={assignee.id === assigneeId}
              onClick={() => {
                onChange(assignee.id)
                setOpen(false)
              }}
              testId={`next-step-assignee-${assignee.id}`}
            >
              <span className="flex items-center justify-center shrink-0 rounded-full size-5 bg-[#1E1B17] text-[#F0EDE6] font-semibold text-[9px] leading-3">
                {assignee.initials}
              </span>
              <span className="text-foreground text-[13px] leading-4 truncate">
                {label(assignee)}
              </span>
            </PickerOption>
          ))}
        </PickerPopover>
      ) : null}
    </>
  )
}

// Shared portaled popover for the collapsed pickers — same
// surface treatment as OwnerPopover (CON-206). Portals to
// document.body so it escapes the slideover's clip; closes
// on Escape / outside click.
function PickerPopover({
  anchor,
  ariaLabel,
  onClose,
  children,
}: {
  anchor: HTMLElement
  ariaLabel: string
  onClose: () => void
  children: React.ReactNode
}) {
  const position = useAnchoredPosition(anchor, PICKER_WIDTH)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // DOM KeyboardEvent — disambiguate from React's imported
    // `KeyboardEvent` type used by the title input's handler.
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation()
        onClose()
      }
    }
    function onPointer(e: MouseEvent) {
      const target = e.target as Node
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        !anchor.contains(target)
      ) {
        onClose()
      }
    }
    document.addEventListener("keydown", onKey, true)
    document.addEventListener("mousedown", onPointer)
    return () => {
      document.removeEventListener("keydown", onKey, true)
      document.removeEventListener("mousedown", onPointer)
    }
  }, [anchor, onClose])

  return createPortal(
    <div
      ref={popoverRef}
      role="listbox"
      aria-label={ariaLabel}
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        zIndex: 60,
        width: PICKER_WIDTH,
      }}
      className="rounded-xl bg-white border border-[#E5DFD3] shadow-[0px_24px_48px_-8px_#1E1B1726] py-2 flex flex-col"
    >
      {children}
    </div>,
    document.body,
  )
}

function PickerOption({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean
  onClick: () => void
  testId?: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#F7F5F0] cursor-pointer"
    >
      {children}
      {active ? <CheckMark /> : null}
    </button>
  )
}

// "Pick date" affordance — a calendar glyph that overlays a
// transparent `datetime-local` input so a click on the chip
// opens the native picker. Active when no quick-due preset is
// selected (i.e. the user picked a custom date).
function CustomDuePicker({
  active,
  value,
  onChange,
}: {
  active: boolean
  value: Date | null
  onChange: (localValue: string) => void
}) {
  const { t } = useLingui()
  return (
    <label
      data-testid="next-step-due-pick-date"
      aria-label={t`Pick a date`}
      className={
        active
          ? "relative inline-flex items-center gap-1.5 rounded-full bg-[#1E1B17] text-[#F0EDE6] text-[12px] leading-4 px-3 py-1 font-medium cursor-pointer"
          : "relative inline-flex items-center gap-1.5 rounded-full bg-white border border-[#C9C2B6] text-foreground text-[12px] leading-4 px-3 py-1 font-medium hover:bg-[#F7F5F0] cursor-pointer"
      }
    >
      <CalendarGlyph />
      <span><Trans>Pick date</Trans></span>
      <input
        type="datetime-local"
        aria-label={t`Custom due date`}
        value={value ? toLocalInputValue(value) : ""}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 size-full opacity-0 cursor-pointer"
      />
    </label>
  )
}

function Chip({
  active,
  onClick,
  children,
  ...rest
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick">) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        active
          ? "inline-flex items-center gap-1.5 rounded-full bg-[#1E1B17] text-[#F0EDE6] text-[12px] leading-4 px-3 py-1 font-medium"
          : "inline-flex items-center gap-1.5 rounded-full bg-white border border-[#C9C2B6] text-foreground text-[12px] leading-4 px-3 py-1 font-medium hover:bg-[#F7F5F0]"
      }
      {...rest}
    >
      {children}
    </button>
  )
}

// Accepts the raw method (incl. call-logged variants / null) so
// the collapsed trigger glyph matches an existing row's channel
// family — whatsapp_call reads as WhatsApp, voice_call_outbound
// as a call, etc. — consistent with `methodLabel`.
function MethodGlyph({method}: {method: string | null}) {
  if (!method) return null
  if (method.startsWith("whatsapp")) {
    return (
      <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M8 2C4.69 2 2 4.69 2 8C2 9.16 2.34 10.24 2.91 11.16L2 14L4.94 13.13C5.83 13.67 6.88 14 8 14C11.31 14 14 11.31 14 8C14 4.69 11.31 2 8 2Z"
          fill="#25D366"
        />
      </svg>
    )
  }
  if (method.startsWith("voice_call")) return <span aria-hidden="true">📞</span>
  if (method.startsWith("email")) return <span aria-hidden="true">✉︎</span>
  return null
}

function Chevron() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-[#9A938A]"
    >
      <path
        d="M4 6L8 10L12 6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CalendarGlyph() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <rect
        x="2.5"
        y="3"
        width="11"
        height="10.5"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.3"
      />
      <path
        d="M2.5 6H13.5M5.5 1.75V4M10.5 1.75V4"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CheckMark() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="ml-auto shrink-0"
    >
      <path
        d="M3 8L6.5 11.5L13 4.5"
        stroke="#1E1B17"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function formatDueAt(date: Date): string {
  return date.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

// Date → the local-time string a `datetime-local` input wants
// (YYYY-MM-DDTHH:mm in the user's timezone, no Z).
function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0")
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}
